namespace $ {
	export type $shm_hitalama_benchmarks_client_state = {
		active: { status: string, started: string, report: any, error?: string } | null
		/** Transient publication queue, never persisted or used as table data. */
		history: { id: string, report: any }[]
	}

	/** Client controller. The child frame owns fresh CRDT caches after each reload. */
	export class $shm_hitalama_benchmarks_client extends $mol_object {
		private generation = 0
		private run_started = 0
		private frame_element?: HTMLIFrameElement
		private pending?: { id: string, resolve: (value: any)=> void, reject: (error: Error)=> void, timer: number }
		private listener?: (event: MessageEvent)=> void
		private current_state?: $shm_hitalama_benchmarks_client_state
		private deadline=Infinity
		private current_error=''
		private current_frame=''
		@ $mol_mem archive() { return new this.$.$shm_hitalama_benchmarks_archive }
		@ $mol_action archive_flush() {
			// Baza owns persisted reports. Discard the obsolete parallel localStorage history.
			this.$.$mol_state_local.value('hitalama.benchmark.history', null)
			for (const run of this.state().history) {
				const meta = run.report.meta
				if (!meta) throw Error('У нового прогона отсутствуют метаданные')
				if (!this.archive().save(meta, run.report.rows ?? [])) return false
			}
			if (this.state().history.length) this.state({...this.state(), history: []})
			return true
		}
		@ $mol_mem error(next?:string){if(next!==undefined)this.current_error=next;return this.current_error}
		@ $mol_mem frame(next?:string){if(next!==undefined)this.current_frame=next;return this.current_frame}
		@ $mol_mem state( next?: $shm_hitalama_benchmarks_client_state ) {
			if(next)this.current_state=next
			return this.current_state ??= {active:null,history:[]}
		}
		connected() { return true }
		@ $mol_mem listen() {
			this.listener = event=> {
				if(event.origin !== location.origin || event.source !== this.frame_element?.contentWindow)return
				const data=event.data
				if(data?.benchmark !== 'hitalama-client-v1' || data.id !== this.pending?.id)return
				const pending=this.pending!
				clearTimeout(pending.timer);this.pending=undefined
				if(data.error)pending.reject(Error(data.error))
				else pending.resolve(data.value)
			}
			addEventListener('message',this.listener)
			return true
		}
		bind(frame:HTMLIFrameElement){this.frame_element=frame}
		private request(command:string,payload?:any):Promise<any>{
			if(this.pending)throw Error('Предыдущая операция ещё выполняется')
			const id=crypto.randomUUID()
			return new Promise((resolve,reject)=>{
				const timer=window.setTimeout(()=>{this.pending=undefined;reject(Error('Операция превысила 120 секунд'))},Math.max(1,Math.min(120000,this.deadline-performance.now())))
				this.pending={id,resolve,reject,timer}
				this.frame_element!.contentWindow!.postMessage({benchmark:'hitalama-client-v1',id,command,payload},location.origin)
			})
		}
		private boot(token:string):Promise<void>{
			return new Promise((resolve,reject)=>{
				const timer=window.setTimeout(()=>{this.pending=undefined;reject(Error('Загрузка тестового приложения превысила 120 секунд'))},Math.max(1,Math.min(120000,this.deadline-performance.now())))
				this.pending={id:'boot:'+token,resolve,reject,timer}
				// Use the shipped application bundle, not a dev-builder-only module URL.
				const uri = new URL(location.href)
				const guest = uri.searchParams.get('guest_app') === '1'
				uri.search = ''
				if(guest)uri.searchParams.set('guest_app', '1')
				uri.searchParams.set('benchmark_runner', token)
				uri.hash = '#!benchmark=' + token
				this.frame(uri.toString())
			})
		}
		@ $mol_action send(message:any){
			this.listen()
			if(message.command==='stop'){this.cancel();return}
			if(message.command!=='start'||this.state().active?.status==='running')return
			const generation=++this.generation
			this.run_started=performance.now()
			const started=new Date().toISOString()
			const report={protocol:'table2-client-v2',meta:{...message.meta,id:crypto.randomUUID(),started},limitMs:120000,observed:true,publication:'chunks32',allocation:'production',querySnapshots:false,statusAfterReload:true,addAfterReload:true,rows:[] as any[]}
			this.error('')
			this.state({...this.state(),active:{status:'running',started,report}})
			// Event-based browser orchestration is outside wire. Child commands use stable wire actions.
			void this.run(message.config,report,generation).catch(error=>{
				if(generation!==this.generation)return
				this.error(String(error.message??error));this.finish('failed')
				this.frame('about:blank')
			})
		}
		private async run(config:any,report:any,generation:number){
			for(const count of config.counts)for(const properties of config.properties)for(const storage of config.modes)for(const packing of config.packings){
				if(generation!==this.generation)return
				const row:any={count,properties,storage,packing,status:'running',phase:'schema',measurements:{},reloads:[]}
				report.rows.push(row);this.publish(report)
				try {
				this.deadline=performance.now()+120000
				await this.boot(crypto.randomUUID())
				if(generation!==this.generation)return
				const fixture=await this.request('prepare',{storage,properties,packing,access:'private',allocation:'production'})
				row.fixture=fixture
					for(const metric of ['create','f5','cold','warm','status','add']){
						if(generation!==this.generation)return
						if(metric==='status'||metric==='add'){
							row.phase=metric+'-reload';this.publish(report)
							this.deadline=performance.now()+120000
							await this.boot(crypto.randomUUID())
							if(generation!==this.generation)return
							await this.request('attach',{config:{storage,properties,packing,allocation:'production'},fixture,count})
							row[metric+'AfterReload']=true
						}
					row.phase=metric;this.publish(report)
					const start=performance.now()
					this.deadline=start+120000
					if(metric==='f5'){
							await this.request('persist')
							for(let sample=0;sample<3;sample++){
								// The limit applies to each reload, not the sum of three samples.
								this.deadline=performance.now()+120000
							const reloadStart=performance.now()
							await this.boot(crypto.randomUUID())
							if(generation!==this.generation)return
							await this.request('attach',{config:{storage,properties,packing,allocation:'production'},fixture,count})
							row.reloads.push(performance.now()-reloadStart)
						}
						row.measurements.f5=[...row.reloads].sort((a:number,b:number)=>a-b)[1]
					}else{
						const value=await this.request(metric,{count})
						row.measurements[metric]=performance.now()-start
						if(value?.found!==undefined)(row.found??={})[metric]=value.found
						}
						// Validate identities outside the timed operation, not just the count.
						if(['cold','warm','status'].includes(metric)){
							this.deadline=performance.now()+120000
							await this.request('verify-query',{metric,count})
							;(row.verifiedQueries??=[]).push(metric)
						}
						this.publish(report)
					}
					row.phase='verify-reload';this.publish(report)
					this.deadline=performance.now()+120000
					await this.request('persist')
					this.deadline=performance.now()+120000
					await this.boot(crypto.randomUUID())
					if(generation!==this.generation)return
					await this.request('attach',{config:{storage,properties,packing,allocation:'production'},fixture,count:count+1})
					row.phase='verify-values';this.publish(report)
					this.deadline=performance.now()+120000
					for(let from=0;from<count+1;from+=32){
						if(generation!==this.generation)return
						await this.request('verify',{from,until:Math.min(from+32,count+1)})
					}
					row.verified={records:count+1,properties,afterReload:true}
				row.phase='complete';row.status='passed';this.publish(report)
				} catch(error) {
					if(generation!==this.generation)return
					row.error=String((error as Error).message)
					row.status=row.error.includes('120 секунд')?'over-limit':'failed'
					if(row.status==='over-limit')row.metricLimits={[row.phase]:true}
					this.frame('about:blank');this.publish(report)
				}
			}
			this.finish(report.rows.some((row:any)=>row.status==='failed')?'failed':'complete')
		}
		private publish(report:any){
			report.meta.totalMs=performance.now()-this.run_started
			const state=this.state()
			this.state({...state,active:{...state.active!,report:structuredClone(report)}})
		}
		private finish(status:string){
			const state=this.state(),active={...state.active!,status},id=active.report.meta.id
			active.report.meta.totalMs=performance.now()-this.run_started
			if(status==='stopped')active.report.rows=active.report.rows.map((row:any)=>row.status==='running'?{...row,status:'stopped'}:row)
			const history=[{id,report:active.report},...state.history]
			this.state({active,history})
		}
		private cancel(){
			++this.generation
			if(this.pending){clearTimeout(this.pending.timer);this.pending.reject(Error('Остановлен'));this.pending=undefined}
			this.frame('about:blank')
			if(this.state().active?.status==='running')this.finish('stopped')
		}
		destructor(){this.cancel();if(this.listener)removeEventListener('message',this.listener)}
	}
}
